import * as React from 'react';
import { useList } from '@refinedev/core';
import type {
  SegmentConditionValue,
  SegmentFieldMeta,
  SegmentOperator,
  SegmentProductRef,
} from '@masters-club/validation';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { ProductRefInput } from './ProductRefInput';
import { type EntityLike, resolveEntityLabel } from './segment-labels';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DatePicker } from '@/components/ui/date-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DateRange } from 'react-day-picker';

interface SegmentValueInputProps {
  meta: SegmentFieldMeta;
  operator: SegmentOperator;
  value: SegmentConditionValue | undefined;
  onChange: (value: SegmentConditionValue | undefined) => void;
  disabled?: boolean;
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((item) => typeof item === 'string');
}

function isTupleOfStrings(v: unknown): v is [string, string] {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    typeof v[0] === 'string' &&
    typeof v[1] === 'string'
  );
}

function isTupleOfNumbers(v: unknown): v is [number, number] {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    typeof v[0] === 'number' &&
    typeof v[1] === 'number'
  );
}

function isProductRef(v: unknown): v is SegmentProductRef {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  if (!('targetId' in v) || typeof v.targetId !== 'number') return false;
  if ('tierId' in v && v.tierId !== undefined && typeof v.tierId !== 'number') {
    return false;
  }
  return true;
}

/**
 * Фабрика value-контрола условия: рендерит подходящий UI-виджет
 * в зависимости от kind поля и оператора.
 */
export function SegmentValueInput({
  meta,
  operator,
  value,
  onChange,
  disabled = false,
}: SegmentValueInputProps) {
  // isSet / isNotSet — значение не нужно
  if (operator === 'isSet' || operator === 'isNotSet') {
    return null;
  }

  if (meta.kind === 'boolean') {
    const checked = value === true;
    return (
      <Switch
        checked={checked}
        onCheckedChange={(v) => onChange(v)}
        disabled={disabled}
      />
    );
  }

  if (meta.kind === 'string') {
    const strVal = typeof value === 'string' ? value : '';
    return (
      <Input
        value={strVal}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Значение"
        disabled={disabled}
        className="w-full"
      />
    );
  }

  if (meta.kind === 'number') {
    if (operator === 'between') {
      const from =
        isTupleOfNumbers(value) ? String(value[0]) : '';
      const to =
        isTupleOfNumbers(value) ? String(value[1]) : '';
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={from}
            onChange={(e) => {
              const fromNum = Number(e.target.value);
              const toNum = isTupleOfNumbers(value) ? value[1] : 0;
              onChange([fromNum, toNum]);
            }}
            placeholder="От"
            disabled={disabled}
            className="w-full"
          />
          <span className="text-muted-foreground text-sm shrink-0">—</span>
          <Input
            type="number"
            value={to}
            onChange={(e) => {
              const toNum = Number(e.target.value);
              const fromNum = isTupleOfNumbers(value) ? value[0] : 0;
              onChange([fromNum, toNum]);
            }}
            placeholder="До"
            disabled={disabled}
            className="w-full"
          />
        </div>
      );
    }
    const numVal = typeof value === 'number' ? String(value) : '';
    return (
      <Input
        type="number"
        value={numVal}
        onChange={(e) => onChange(Number(e.target.value))}
        placeholder="Значение"
        disabled={disabled}
        className="w-full"
      />
    );
  }

  if (meta.kind === 'date') {
    if (operator === 'withinLastDays') {
      const numVal = typeof value === 'number' ? String(value) : '';
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={numVal}
            onChange={(e) => onChange(Number(e.target.value))}
            placeholder="Кол-во дней"
            disabled={disabled}
            className="w-full"
          />
          <span className="text-muted-foreground text-sm shrink-0 whitespace-nowrap">дней</span>
        </div>
      );
    }
    if (operator === 'between') {
      const fromDate = isTupleOfStrings(value) ? new Date(value[0]) : null;
      const toDate = isTupleOfStrings(value) ? new Date(value[1]) : null;
      const rangeValue: DateRange | null =
        fromDate || toDate ? { from: fromDate ?? undefined, to: toDate ?? undefined } : null;
      return (
        <DateRangePicker
          value={rangeValue}
          onChange={(range) => {
            if (!range || !range.from) {
              onChange(undefined);
              return;
            }
            const fromStr = range.from.toISOString();
            const toStr = range.to ? range.to.toISOString() : range.from.toISOString();
            onChange([fromStr, toStr]);
          }}
          disabled={disabled}
        />
      );
    }
    const dateVal = typeof value === 'string' ? new Date(value) : null;
    return (
      <DatePicker
        value={dateVal}
        onChange={(date) => onChange(date ? date.toISOString() : undefined)}
        disabled={disabled}
      />
    );
  }

  if (meta.kind === 'enum') {
    const opts: MultiSelectOption[] = (meta.enumOptions ?? []).map((o) => ({
      label: o.label,
      value: o.value,
    }));

    if (operator === 'in' || operator === 'notIn') {
      const selectedVals = isStringArray(value) ? value : [];
      return (
        <MultiSelect
          options={opts}
          value={selectedVals}
          onChange={(vals) => onChange(vals)}
          placeholder="Выберите значения"
          disabled={disabled}
        />
      );
    }

    // eq / neq
    const singleVal = typeof value === 'string' ? value : '';
    return (
      <Select
        value={singleVal}
        onValueChange={(v) => onChange(v)}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Выберите значение" />
        </SelectTrigger>
        <SelectContent>
          {opts.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (meta.kind === 'productRef') {
    return (
      <ProductRefInput
        resource={meta.optionsResource ?? ''}
        supportsTier={meta.supportsTier ?? false}
        value={isProductRef(value) ? value : undefined}
        onChange={(v) => onChange(v)}
        disabled={disabled}
      />
    );
  }

  if (meta.kind === 'reference') {
    return (
      <ReferenceValueInput
        meta={meta}
        operator={operator}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  return null;
}

interface ReferenceValueInputProps {
  meta: SegmentFieldMeta;
  operator: SegmentOperator;
  value: SegmentConditionValue | undefined;
  onChange: (value: SegmentConditionValue | undefined) => void;
  disabled?: boolean;
}

type ResourceItem = EntityLike;

function ReferenceValueInput({
  meta,
  value,
  onChange,
  disabled = false,
}: ReferenceValueInputProps) {
  const resource = meta.optionsResource ?? '';
  const { result } = useList<ResourceItem>({
    resource,
    pagination: { pageSize: 200, mode: 'server' },
  });

  const opts: MultiSelectOption[] = (result?.data ?? []).map((item: ResourceItem) => ({
    value: String(item.id),
    label: resolveEntityLabel(item),
  }));

  const selectedVals = isStringArray(value) ? value : [];

  return (
    <MultiSelect
      options={opts}
      value={selectedVals}
      onChange={(vals) => onChange(vals)}
      placeholder="Выберите значения"
      disabled={disabled}
    />
  );
}
